import { IterationRecord } from '../../types';

// ============================================
// 17 ITERATIONS OF AERATOR DEVELOPMENT
// ============================================

export const iterationHistory: IterationRecord[] = [
    {
        iterationNumber: 1,
        iterationDate: '2025-08-01',
        iterationName: 'LLM-Generated Concept (Aerator Pro Baseline)',
        phase: 'Concept',
        reviewers: ['WK', 'MC', 'JS'],
        changes: [
            {
                changeId: 'I1-C001',
                category: 'Design',
                affectedItems: ['BOM-ALL'],
                description: 'Initial BOM created with 18 components',
                rationale: 'Establish baseline design based on customer requirements',
                initiatedBy: 'WK',
                approvedBy: 'MC',
                dateImplemented: '2025-08-01',
                costImpact: 0
            },
            {
                changeId: 'I1-C002',
                category: 'FMEA',
                affectedItems: ['FMEA-ALL'],
                description: 'Initial FMEA created with 17 failure modes',
                rationale: 'Baseline risk assessment',
                initiatedBy: 'JS',
                approvedBy: 'WK',
                dateImplemented: '2025-08-05'
            }
        ],
        fmeaSnapshot: {
            totalRPN: 1869,
            avgRPN: 110,
            highRiskCount: 8,
            criticalRiskCount: 3
        },
        bomSnapshot: {
            totalComponents: 18,
            validatedComponents: 0,
            totalCost: 12.50,
            totalMass: 145
        },
        notes: 'LLM Baseline Design. Major concerns: Physics violations in shaft thermal expansion prediction.',
        projectName: 'Aerator Pro',
        nextActions: ['Run Magnus Physics Engine', 'Identify hidden failure modes']
    },

    {
        iterationNumber: 2,
        iterationDate: '2025-08-10',
        iterationName: 'Magnus Physics Correction',
        phase: 'Design',
        reviewers: ['WK', 'MC'],
        changes: [
            {
                changeId: 'I2-C001',
                category: 'Design',
                affectedItems: ['12'],
                description: 'Added molded strain relief to wire exit points',
                rationale: 'Prevent wire flex fatigue identified in FMEA-003',
                initiatedBy: 'WK',
                approvedBy: 'MC',
                dateImplemented: '2025-08-10',
                costImpact: 0.15,
                rpnImpact: {
                    before: 144,
                    after: 72,
                    reduction: 50
                }
            }
        ],
        fmeaSnapshot: {
            totalRPN: 1797,
            avgRPN: 106,
            highRiskCount: 8,
            criticalRiskCount: 3
        },
        bomSnapshot: {
            totalComponents: 18,
            validatedComponents: 0,
            totalCost: 12.65,
            totalMass: 147
        },
        notes: 'First major RPN reduction. Wire fatigue risk cut in half.',
        projectName: 'Aerator Pro',
        nextActions: ['Test wire strain relief', 'Continue material selection']
    },

    {
        iterationNumber: 3,
        iterationDate: '2025-08-20',
        iterationName: 'Battery Contact Corrosion Protection',
        phase: 'Design',
        reviewers: ['MC', 'JS'],
        changes: [
            {
                changeId: 'I3-C001',
                category: 'Material',
                affectedItems: ['4', '5', 'MAT-STEEL-SPRING-001', 'MAT-STEEL-NI-001'],
                description: 'Changed battery contacts from bare steel to nickel-plated',
                rationale: 'Prevent corrosion identified in FMEA-004',
                initiatedBy: 'MC',
                approvedBy: 'WK',
                dateImplemented: '2025-08-20',
                costImpact: 0.25
            }
        ],
        fmeaSnapshot: {
            totalRPN: 1797,
            avgRPN: 106,
            highRiskCount: 8,
            criticalRiskCount: 3
        },
        bomSnapshot: {
            totalComponents: 18,
            validatedComponents: 2,
            totalCost: 12.90,
            totalMass: 147
        },
        notes: 'Material upgrade complete. No immediate RPN change pending validation testing.',
        projectName: 'Aerator Pro',
        nextActions: ['Salt spray test battery contacts', 'Begin seal material research']
    },

    {
        iterationNumber: 4,
        iterationDate: '2025-09-01',
        iterationName: '304 Stainless Steel Selection',
        phase: 'Design',
        reviewers: ['WK', 'JS'],
        changes: [
            {
                changeId: 'I4-C001',
                category: 'Material',
                affectedItems: ['13', '14', 'MAT-SS304-001'],
                description: 'Specified 304 stainless steel for shaft and whisk',
                rationale: 'Food-safe material requirement, FDA compliant',
                initiatedBy: 'WK',
                approvedBy: 'JS',
                dateImplemented: '2025-09-01',
                costImpact: 0.80
            },
            {
                changeId: 'I4-C002',
                category: 'FMEA',
                affectedItems: ['FMEA-015'],
                description: 'Updated FMEA-015 O rating from 4 to 2',
                rationale: '304 SS eliminates corrosion risk',
                initiatedBy: 'JS',
                approvedBy: 'WK',
                dateImplemented: '2025-09-01',
                rpnImpact: {
                    before: 160,
                    after: 80,
                    reduction: 50
                }
            }
        ],
        fmeaSnapshot: {
            totalRPN: 1717,
            avgRPN: 101,
            highRiskCount: 7,
            criticalRiskCount: 3
        },
        bomSnapshot: {
            totalComponents: 18,
            validatedComponents: 4,
            totalCost: 13.70,
            totalMass: 149
        },
        notes: 'Major corrosion risk eliminated. All food-contact surfaces now FDA compliant.',
        projectName: 'Aerator Pro',
        nextActions: ['Food safety certification', 'Begin seal material testing']
    },

    {
        iterationNumber: 5,
        iterationDate: '2025-09-15',
        iterationName: 'High-Impact ABS Housing Upgrade',
        phase: 'Prototype',
        reviewers: ['WK', 'MC', 'JS'],
        changes: [
            {
                changeId: 'I5-C001',
                category: 'Material',
                affectedItems: ['2', '9', 'MAT-ABS-001'],
                description: 'Upgraded housing from standard ABS to high-impact ABS (HI-95)',
                rationale: 'Meet drop test requirement (1m onto concrete)',
                initiatedBy: 'WK',
                approvedBy: 'MC',
                dateImplemented: '2025-09-15',
                costImpact: 0.40
            },
            {
                changeId: 'I5-C002',
                category: 'FMEA',
                affectedItems: ['FMEA-001'],
                description: 'Reduced FMEA-001 O rating from 5 to 3',
                rationale: 'Drop test validated with high-impact material',
                initiatedBy: 'JS',
                approvedBy: 'WK',
                dateImplemented: '2025-09-20',
                rpnImpact: {
                    before: 240,
                    after: 144,
                    reduction: 40
                }
            }
        ],
        fmeaSnapshot: {
            totalRPN: 1621,
            avgRPN: 95,
            highRiskCount: 7,
            criticalRiskCount: 3
        },
        bomSnapshot: {
            totalComponents: 18,
            validatedComponents: 6,
            totalCost: 14.10,
            totalMass: 150
        },
        testResults: {
            testsCompleted: ['Drop test 1m'],
            testsPassed: ['Drop test 1m'],
            testsFailed: []
        },
        notes: 'First prototype tested successfully. Drop test passed!',
        projectName: 'Aerator Pro',
        nextActions: ['Continue seal development', 'Begin motor qualification']
    },

    {
        iterationNumber: 6,
        iterationDate: '2025-09-25',
        iterationName: 'Seal Material Research - Viton Selection',
        phase: 'Prototype',
        reviewers: ['WK', 'JS'],
        changes: [
            {
                changeId: 'I6-C001',
                category: 'Material',
                affectedItems: ['16', 'MAT-VITON-001'],
                description: 'Changed shaft seal from silicone to Viton FKM',
                rationale: 'Silicone failed hot liquid test (80°C milk). Viton rated to 200°C',
                initiatedBy: 'WK',
                approvedBy: 'JS',
                dateImplemented: '2025-09-25',
                costImpact: 1.20,
                rpnImpact: {
                    before: 270,
                    after: 162,
                    reduction: 40
                }
            },
            {
                changeId: 'I6-C002',
                category: 'FMEA',
                affectedItems: ['FMEA-016'],
                description: 'Updated FMEA-016: O from 5 to 3 based on Viton properties',
                rationale: 'Material upgrade reduces occurrence',
                initiatedBy: 'WK',
                approvedBy: 'JS',
                dateImplemented: '2025-09-25'
            }
        ],
        fmeaSnapshot: {
            totalRPN: 1513,
            avgRPN: 89,
            highRiskCount: 6,
            criticalRiskCount: 3
        },
        bomSnapshot: {
            totalComponents: 18,
            validatedComponents: 7,
            totalCost: 15.30,
            totalMass: 151
        },
        testResults: {
            testsCompleted: ['Hot liquid seal test (80°C)'],
            testsPassed: ['Hot liquid seal test (80°C)'],
            testsFailed: []
        },
        notes: 'Critical seal issue resolved. Viton passes hot milk test.',
        projectName: 'Aerator Pro',
        nextActions: ['Long-term seal testing', 'Switch selection']
    },

    {
        iterationNumber: 7,
        iterationDate: '2025-10-01',
        iterationName: 'TPE Switch Gasket Implementation',
        phase: 'Prototype',
        reviewers: ['JS', 'MC'],
        changes: [
            {
                changeId: 'I7-C001',
                category: 'Material',
                affectedItems: ['10', 'MAT-TPE-001'],
                description: 'Changed switch gasket from silicone to TPE',
                rationale: 'Better compression set resistance for IPX4 sealing',
                initiatedBy: 'JS',
                approvedBy: 'WK',
                dateImplemented: '2025-10-01',
                costImpact: 0.10
            },
            {
                changeId: 'I7-C002',
                category: 'FMEA',
                affectedItems: ['FMEA-008'],
                description: 'Reduced FMEA-008: O from 6 to 4',
                rationale: 'TPE has better long-term compression set performance',
                initiatedBy: 'JS',
                approvedBy: 'WK',
                dateImplemented: '2025-10-05',
                rpnImpact: {
                    before: 324,
                    after: 216,
                    reduction: 33
                }
            }
        ],
        fmeaSnapshot: {
            totalRPN: 1405,
            avgRPN: 83,
            highRiskCount: 5,
            criticalRiskCount: 3
        },
        bomSnapshot: {
            totalComponents: 18,
            validatedComponents: 9,
            totalCost: 15.40,
            totalMass: 151
        },
        testResults: {
            testsCompleted: ['IPX4 splash test'],
            testsPassed: ['IPX4 splash test'],
            testsFailed: []
        },
        notes: 'IPX4 compliance achieved! Second highest RPN now addressed.',
        projectName: 'Aerator Pro',
        nextActions: ['Motor upgrade research', 'Dual seal concept']
    },

    {
        iterationNumber: 8,
        iterationDate: '2025-10-10',
        iterationName: 'Supplier Qualification (Switch & Contacts)',
        phase: 'Prototype',
        reviewers: ['JS', 'MC', 'WK'],
        changes: [
            {
                changeId: 'I8-C001',
                category: 'Supplier',
                affectedItems: ['4', '5', '8'],
                description: 'Qualified ReliableSwitch Corp and SpringTech Co suppliers',
                rationale: 'Supplier life test data validates reliability claims',
                initiatedBy: 'MC',
                approvedBy: 'WK',
                dateImplemented: '2025-10-10'
            },
            {
                changeId: 'I8-C002',
                category: 'Test',
                affectedItems: ['TEST-CONTACT-001', 'TEST-SWITCH-CYCLE-001'],
                description: 'Completed salt spray and cycle testing',
                rationale: 'Validate supplier claims',
                initiatedBy: 'JS',
                approvedBy: 'MC',
                dateImplemented: '2025-10-15'
            }
        ],
        fmeaSnapshot: {
            totalRPN: 1405,
            avgRPN: 83,
            highRiskCount: 5,
            criticalRiskCount: 3
        },
        bomSnapshot: {
            totalComponents: 18,
            validatedComponents: 12,
            totalCost: 15.40,
            totalMass: 151
        },
        testResults: {
            testsCompleted: ['Salt spray 48h', 'Switch cycle 10k'],
            testsPassed: ['Salt spray 48h', 'Switch cycle 10k'],
            testsFailed: []
        },
        notes: 'Supplier validation complete. No design changes this iteration.',
        projectName: 'Aerator Pro',
        nextActions: ['Motor technology review', 'Cost reduction opportunities']
    },

    {
        iterationNumber: 9,
        iterationDate: '2025-10-15',
        iterationName: 'Customer Requirements Update',
        phase: 'Validation',
        reviewers: ['WK', 'MC'],
        changes: [
            {
                changeId: 'I9-C001',
                category: 'Design',
                affectedItems: ['1', '14'],
                description: 'Customer requested 220mm total length and larger whisk',
                rationale: 'Market research shows preference for longer handle and 35mm whisk',
                initiatedBy: 'WK',
                approvedBy: 'MC',
                dateImplemented: '2025-10-15'
            },
            {
                changeId: 'I9-C002',
                category: 'Design',
                affectedItems: ['14'],
                description: 'Increased whisk diameter from 25mm to 35mm',
                rationale: 'Better aeration performance requested by customer',
                initiatedBy: 'WK',
                approvedBy: 'MC',
                dateImplemented: '2025-10-18',
                costImpact: 0.30
            }
        ],
        fmeaSnapshot: {
            totalRPN: 1405,
            avgRPN: 83,
            highRiskCount: 5,
            criticalRiskCount: 3
        },
        bomSnapshot: {
            totalComponents: 18,
            validatedComponents: 12,
            totalCost: 15.70,
            totalMass: 155
        },
        notes: 'Design updated per customer requirements. Length now 220mm.',
        projectName: 'Aerator Pro',
        nextActions: ['Validate new dimensions', 'Review motor torque requirement']
    },

    {
        iterationNumber: 10,
        iterationDate: '2025-10-20',
        iterationName: 'Dual Seal Redundancy Concept',
        phase: 'Validation',
        reviewers: ['WK', 'JS'],
        changes: [
            {
                changeId: 'I10-C001',
                category: 'Design',
                affectedItems: ['15', '17', 'MAT-NBR-001'],
                description: 'Added second seal (NBR backup) for redundancy',
                rationale: 'Shaft seal is critical function - single point of failure unacceptable',
                initiatedBy: 'WK',
                approvedBy: 'JS',
                dateImplemented: '2025-10-30',
                costImpact: 0.15,
                rpnImpact: {
                    before: 162,
                    after: 108,
                    reduction: 33
                }
            },
            {
                changeId: 'I10-C002',
                category: 'FMEA',
                affectedItems: ['FMEA-016'],
                description: 'Updated FMEA-016: O from 3 to 2 with dual seal design',
                rationale: 'Redundant seals significantly reduce leak probability',
                initiatedBy: 'WK',
                approvedBy: 'JS',
                dateImplemented: '2025-11-01'
            }
        ],
        fmeaSnapshot: {
            totalRPN: 1351,
            avgRPN: 79,
            highRiskCount: 4,
            criticalRiskCount: 3
        },
        bomSnapshot: {
            totalComponents: 19,
            validatedComponents: 13,
            totalCost: 15.85,
            totalMass: 156
        },
        notes: 'Major reliability improvement. Dual seal eliminates single point of failure.',
        projectName: 'Aerator Pro',
        nextActions: ['Test dual seal design', 'Motor upgrade decision']
    },

    {
        iterationNumber: 11,
        iterationDate: '2025-10-22',
        iterationName: 'Ceramic Bearing Noise Reduction',
        phase: 'Validation',
        reviewers: ['WK', 'MC'],
        changes: [
            {
                changeId: 'I11-C001',
                category: 'Material',
                affectedItems: ['7', 'MAT-CERAMIC-001'],
                description: 'Upgraded motor bearings from steel to ceramic (Silicon Nitride)',
                rationale: 'Customer complaints about noise. Ceramic bearings 40% quieter',
                initiatedBy: 'WK',
                approvedBy: 'MC',
                dateImplemented: '2025-10-22',
                costImpact: 2.50
            }
        ],
        fmeaSnapshot: {
            totalRPN: 1351,
            avgRPN: 79,
            highRiskCount: 4,
            criticalRiskCount: 3
        },
        bomSnapshot: {
            totalComponents: 19,
            validatedComponents: 13,
            totalCost: 18.35,
            totalMass: 154
        },
        testResults: {
            testsCompleted: ['Noise measurement test'],
            testsPassed: ['Noise measurement test'],
            testsFailed: []
        },
        notes: 'Noise reduced from 72 dB to 55 dB. Customer requirement now met.',
        projectName: 'Aerator Pro',
        nextActions: ['Cost impact review', 'Motor technology decision']
    },

    {
        iterationNumber: 12,
        iterationDate: '2025-10-25',
        iterationName: 'Magnus Optimization (Brushless)',
        phase: 'Validation',
        reviewers: ['WK', 'JS', 'MC'],
        changes: [
            {
                changeId: 'I12-C001',
                category: 'Design',
                affectedItems: ['6'],
                description: 'Replaced brushed DC motor with brushless DC motor',
                rationale: 'Calculated thermal efficiency improvement of 22% and infinite brush life',
                initiatedBy: 'WK',
                approvedBy: 'MC',
                dateImplemented: '2025-10-25',
                costImpact: 3.80,
                rpnImpact: {
                    before: 72,
                    after: 18,
                    reduction: 75
                }
            },
            {
                changeId: 'I12-C002',
                category: 'FMEA',
                affectedItems: ['FMEA-009', 'FMEA-011'],
                description: 'Updated motor FMEA items for brushless design',
                rationale: 'Brushless eliminates brush wear, reduces stall risk',
                initiatedBy: 'JS',
                approvedBy: 'WK',
                dateImplemented: '2025-10-25'
            },
            {
                changeId: 'I12-C003',
                category: 'Supplier',
                affectedItems: ['6', '7'],
                description: 'Qualified TurboMotors Ltd as premium motor supplier',
                rationale: 'Integrated brushless motor with ceramic bearings',
                initiatedBy: 'MC',
                approvedBy: 'WK',
                dateImplemented: '2025-11-01'
            }
        ],
        fmeaSnapshot: {
            totalRPN: 1225,
            avgRPN: 72,
            highRiskCount: 4,
            criticalRiskCount: 3
        },
        bomSnapshot: {
            totalComponents: 19,
            validatedComponents: 14,
            totalCost: 22.15,
            totalMass: 158
        },
        testResults: {
            testsCompleted: ['Motor life test 200h', 'Performance test'],
            testsPassed: ['Motor life test 200h', 'Performance test'],
            testsFailed: []
        },
        notes: 'MAJOR UPGRADE! Brushless motor eliminates failure mode and improves performance.',
        projectName: 'Aerator Pro',
        nextActions: ['Cost reduction review', 'Production readiness assessment']
    },

    {
        iterationNumber: 13,
        iterationDate: '2025-11-05',
        iterationName: 'Design Freeze Candidate',
        phase: 'Validation',
        reviewers: ['WK', 'JS', 'MC'],
        changes: [
            {
                changeId: 'I13-C001',
                category: 'Test',
                affectedItems: ['ALL'],
                description: 'Completed full DVP&R test plan',
                rationale: 'Validate all critical functions before design freeze',
                initiatedBy: 'JS',
                approvedBy: 'WK',
                dateImplemented: '2025-11-10'
            }
        ],
        fmeaSnapshot: {
            totalRPN: 1225,
            avgRPN: 72,
            highRiskCount: 4,
            criticalRiskCount: 3
        },
        bomSnapshot: {
            totalComponents: 19,
            validatedComponents: 16,
            totalCost: 22.15,
            totalMass: 158
        },
        testResults: {
            testsCompleted: ['Drop test', 'IPX4', 'Life test', 'Performance', 'Hot liquid'],
            testsPassed: ['Drop test', 'IPX4', 'Life test', 'Performance', 'Hot liquid'],
            testsFailed: []
        },
        notes: 'All critical tests passed. Design freeze recommended pending cost review.',
        projectName: 'Aerator Pro',
        nextActions: ['Cost reduction analysis', 'Production planning']
    },

    {
        iterationNumber: 14,
        iterationDate: '2025-11-15',
        iterationName: 'Cost Optimization Review',
        phase: 'Validation',
        reviewers: ['MC', 'WK'],
        changes: [
            {
                changeId: 'I14-C001',
                category: 'Design',
                affectedItems: ['ALL'],
                description: 'Cost reduction analysis completed',
                rationale: 'Target cost is $18.00, current $22.15',
                initiatedBy: 'MC',
                approvedBy: 'WK',
                dateImplemented: '2025-11-15'
            }
        ],
        fmeaSnapshot: {
            totalRPN: 1225,
            avgRPN: 72,
            highRiskCount: 4,
            criticalRiskCount: 3
        },
        bomSnapshot: {
            totalComponents: 19,
            validatedComponents: 17,
            totalCost: 22.15,
            totalMass: 158
        },
        notes: 'Cost 23% over target. Decision: Accept higher cost for premium "Pro" version.',
        projectName: 'Aerator Pro',
        nextActions: ['Marketing for premium positioning', 'Production setup']
    },

    {
        iterationNumber: 15,
        iterationDate: '2025-11-20',
        iterationName: 'Production Readiness Review',
        phase: 'Production',
        reviewers: ['WK', 'JS', 'MC'],
        changes: [
            {
                changeId: 'I15-C001',
                category: 'Assembly',
                affectedItems: ['13'],
                description: 'Developed press-fit assembly procedure for shaft coupling',
                rationale: 'Ensure reliable shaft-to-motor connection',
                initiatedBy: 'JS',
                approvedBy: 'WK',
                dateImplemented: '2025-11-20'
            },
            {
                changeId: 'I15-C002',
                category: 'Test',
                affectedItems: ['ALL'],
                description: 'Established end-of-line testing protocol',
                rationale: '100% electrical and motor testing required',
                initiatedBy: 'MC',
                approvedBy: 'WK',
                dateImplemented: '2025-11-22'
            }
        ],
        fmeaSnapshot: {
            totalRPN: 1289,
            avgRPN: 76,
            highRiskCount: 4,
            criticalRiskCount: 3
        },
        bomSnapshot: {
            totalComponents: 20,
            validatedComponents: 18,
            totalCost: 22.25,
            totalMass: 160
        },
        notes: 'Manufacturing processes defined. Ready for pilot production.',
        projectName: 'Aerator Pro',
        nextActions: ['Pilot build', 'Process capability studies']
    },

    {
        iterationNumber: 16,
        iterationDate: '2025-12-01',
        iterationName: 'Pilot Production (50 Units)',
        phase: 'Production',
        reviewers: ['WK', 'JS', 'MC'],
        changes: [
            {
                changeId: 'I16-C001',
                category: 'Test',
                affectedItems: ['ALL'],
                description: 'Pilot build of 50 units completed',
                rationale: 'Validate manufacturing processes and assembly procedures',
                initiatedBy: 'MC',
                approvedBy: 'WK',
                dateImplemented: '2025-12-01'
            },
            {
                changeId: 'I16-C002',
                category: 'Test',
                affectedItems: ['20'],
                description: 'Added product label with CE/UL markings',
                rationale: 'Regulatory compliance requirement',
                initiatedBy: 'MC',
                approvedBy: 'WK',
                dateImplemented: '2025-12-01',
                costImpact: 0.10
            }
        ],
        fmeaSnapshot: {
            totalRPN: 1289,
            avgRPN: 76,
            highRiskCount: 4,
            criticalRiskCount: 3
        },
        bomSnapshot: {
            totalComponents: 20,
            validatedComponents: 19,
            totalCost: 22.35,
            totalMass: 161
        },
        testResults: {
            testsCompleted: ['First article inspection', 'EOL testing 50 units'],
            testsPassed: ['First article inspection', 'EOL testing 50 units'],
            testsFailed: []
        },
        notes: 'Pilot successful! 100% pass rate on EOL testing. Zero defects.',
        projectName: 'Aerator Pro',
        nextActions: ['Scale to full production', 'Final documentation']
    },

    {
        iterationNumber: 17,
        iterationDate: '2025-12-12',
        iterationName: 'Production Release - Aerator Pro V1.0',
        phase: 'Production',
        reviewers: ['WK', 'JS', 'MC'],
        changes: [
            {
                changeId: 'I17-C001',
                category: 'Design',
                affectedItems: ['ALL'],
                description: 'Design freeze - Released to production as Aerator Pro V1.0',
                rationale: 'All validation complete, manufacturing proven',
                initiatedBy: 'WK',
                approvedBy: 'MC',
                dateImplemented: '2025-12-12'
            },
            {
                changeId: 'I17-C002',
                category: 'Design',
                affectedItems: ['ALL'],
                description: 'Final documentation package completed',
                rationale: 'Production ready with full traceability',
                initiatedBy: 'JS',
                approvedBy: 'WK',
                dateImplemented: '2025-12-12'
            }
        ],
        fmeaSnapshot: {
            totalRPN: 1289,
            avgRPN: 76,
            highRiskCount: 4,
            criticalRiskCount: 3
        },
        bomSnapshot: {
            totalComponents: 20,
            validatedComponents: 20,
            totalCost: 22.35,
            totalMass: 161
        },
        notes: '🎉 PRODUCTION RELEASE! 31% RPN reduction from iteration 1. Premium quality achieved.',
        projectName: 'Aerator Pro',
        nextActions: ['Begin production ramp', 'Monitor field performance', 'Plan V2.0 features']
    }
];

// Summary Statistics
export const iterationSummary = {
    totalIterations: 17,
    developmentDuration: '132 days (Aug 1 - Dec 12, 2025)',
    totalRPNReduction: 580, // 1869 → 1289
    percentRPNReduction: 31,
    totalCostIncrease: 9.85, // $12.50 → $22.35
    percentCostIncrease: 79, // Premium version justification
    totalMassIncrease: 16, // 145g → 161g
    criticalUpgrades: 5,
    majorMilestones: [
        'I1: Baseline established',
        'I5: Drop test passed',
        'I6: Viton seal resolved hot liquid issue',
        'I10: Dual seal redundancy',
        'I12: Brushless motor (75% RPN reduction)',
        'I17: Production release'
    ],
    reviewers: ['WK (Wayne K)', 'JS (John S)', 'MC (Maria C)'],
    totalChanges: iterationHistory.reduce((sum, iter) => sum + iter.changes.length, 0)
};

// Export for easy import
export const iterationHistoryInfo = {
    totalIterations: iterationHistory.length,
    totalChanges: iterationSummary.totalChanges,
    generatedDate: '2025-12-12',
    projectName: 'Handheld Milk Frother - Aerator Pro',
    finalVersion: 'V1.0',
    productionReady: true
};
